/**
 * Client-side view of the booking's unified `discount` object — the same shape the
 * backend stores (`routes/utils/discount.js`, `models/schemas/discount.js`):
 *
 *   discount: { rate, amount, fromQuotation, iataEligible, campaignEligible }
 *
 * It lives twice on a booking: once at the top level (summarizing the booking) and
 * once per `bookingInfo[]` vehicle. The three source flags are mutually exclusive.
 *
 * The price fields around it are NOT uniform, so the amount owed is read per origin:
 *   - saved customer-flow bookings and admin edits: `totalPrice` is the amount owed
 *     (already discounted, and it also carries any price the admin set by hand), and
 *     every vehicle holds its share of the discount;
 *   - bookings created from a quotation: `totalPrice` is the PRE-discount figure, the
 *     amount owed sits in `discountedPrice`, and only the booking-level `discount` is
 *     filled in — the per-vehicle ones stay empty;
 *   - pre-payment carts (estimate → confirmation): no `_id` and no trustworthy
 *     booking-level total (the confirmation page recomputes it ignoring the discount),
 *     only per-vehicle `discount` objects. Their `bookingInfo[].total` also excludes
 *     child seats — those are picked after the estimate — while saved customer-flow
 *     bookings fold `childSeatPrice` into it.
 *
 * `totalPriceBeforeDiscount` is deliberately never read: the backend leaves a stale
 * value behind when a booking is edited or its discount removed. The pre-discount price
 * is derived as "amount owed + discount amount" instead, which is always self-consistent.
 */

/** Per-seat child/booster seat price, matching `childSeatPrice` in the backend config. */
export const CHILD_SEAT_PRICE = 1100;

const sumBy = (items, pick) =>
    (Array.isArray(items) ? items : []).reduce((sum, item) => sum + Number(pick(item) || 0), 0);

/** Fill in defaults for any missing field of a discount object. */
export const normalizeDiscount = (discount) => ({
    rate: Number(discount?.rate || 0),
    amount: Number(discount?.amount || 0),
    fromQuotation: Boolean(discount?.fromQuotation),
    iataEligible: Boolean(discount?.iataEligible),
    campaignEligible: Boolean(discount?.campaignEligible),
});

/** True when a discount object actually carries a discount worth displaying. */
export const isDiscountApplied = (discount) => normalizeDiscount(discount).amount > 0;

/**
 * Which source a discount comes from: "campaign", "iata" or "quotation" (admin-set,
 * either from a quotation or from the edit-booking settings), or null when there is none.
 */
export const getDiscountSource = (discount) => {
    const d = normalizeDiscount(discount);
    if (d.amount <= 0) return null;
    if (d.campaignEligible) return "campaign";
    if (d.iataEligible) return "iata";
    // An amount with no source flag can only be an admin-set discount.
    return "quotation";
};

const LABEL_KEYS = {
    campaign: { applied: "campaignDiscountApplied", row: "campaignDiscount" },
    iata: { applied: "commissionDiscountApplied", row: "commissionDiscount" },
    quotation: { applied: "specialDiscountApplied", row: "specialDiscount" },
};

/**
 * Translation keys (unprefixed) for a discount: `applied` for the "… applied" note next
 * to the struck-through price, `row` for the discount line in the fare breakdown.
 */
export const getDiscountLabelKeys = (discount) => LABEL_KEYS[getDiscountSource(discount)] || null;

/**
 * The booking-level discount. Prefers the stored top-level object and falls back to
 * summarizing the per-vehicle ones the way the backend does (rate/flags from the first
 * discounted vehicle, amounts summed) — pre-payment carts only have the per-vehicle ones.
 */
export const getBookingDiscount = (booking) => {
    const topLevel = normalizeDiscount(booking?.discount);
    if (topLevel.amount > 0) return topLevel;

    const items = Array.isArray(booking?.bookingInfo) ? booking.bookingInfo : [];
    const discounts = items.map((item) => normalizeDiscount(item?.discount));
    const source = discounts.find((d) => d.amount > 0);
    if (!source) return topLevel;

    return { ...source, amount: discounts.reduce((sum, d) => sum + d.amount, 0) };
};

/** Total child/booster seats, from the per-vehicle counts or the booking-level ones. */
export const getChildSeatCount = (booking) => {
    const fromVehicles = sumBy(
        (booking?.bookingInfo || []).flatMap((item) => item?.childSeat || []),
        (seat) => seat?.childSeatCount
    );
    if (fromVehicles > 0) return fromVehicles;

    // Quotation bookings keep the seats on the booking instead of on each vehicle.
    return sumBy(booking?.childSeat, (seat) => seat?.childSeatCount);
};

/**
 * Every figure a price display needs — the amount owed, the pre-discount price, the fare
 * and the add-ons — consistent across all three discount sources, both
 * `bookingInfo[].total` conventions and every page of the booking flow.
 */
export const getBookingPriceBreakdown = (booking) => {
    const discount = getBookingDiscount(booking);
    const amount = discount.amount;

    const meetAssistTotal = sumBy(booking?.meetAssist, (item) => item?.meetAssistPrice);
    const itemsTotal = sumBy(booking?.bookingInfo, (item) => item?.total);
    const childSeatCount = getChildSeatCount(booking);
    const childSeatTotal =
        sumBy(booking?.bookingInfo, (item) => item?.childSeatPrice) ||
        childSeatCount * CHILD_SEAT_PRICE;

    const storedTotal = Number(booking?.totalPrice || 0);
    const storedDiscounted = Number(booking?.discountedPrice || 0);
    // Only quotation-created bookings carry a booking-level `discountedPrice`, and for
    // those `totalPrice` is the price before the discount.
    const isQuotationPricing = storedDiscounted > 0 && storedTotal > storedDiscounted;

    // Was this priced by the backend? Only then is the stored total meaningful — the
    // estimate the vehicle-selection page builds has no `_id` and none of the fields the
    // server adds when it prices a booking, and the confirmation page's own `totalPrice`
    // is a running client-side sum.
    const isServerPriced =
        Boolean(booking?._id) ||
        (Array.isArray(booking?.bookingInfo) &&
            booking.bookingInfo.some((item) => item?.vehiclePrice !== undefined));

    let totalPrice;
    if (isServerPriced && isQuotationPricing) {
        totalPrice = storedDiscounted;
    } else if (isServerPriced && storedTotal > 0) {
        totalPrice = storedTotal;
    } else {
        // Pre-payment cart: sum it up ourselves, discount included.
        totalPrice = Math.max(itemsTotal + childSeatTotal + meetAssistTotal - amount, 0);
    }

    const priceBeforeDiscount = totalPrice + amount;

    // Which `total` convention this booking uses: reconcile the vehicle totals and the
    // add-ons against the pre-discount price.
    const childSeatsInItemTotals =
        childSeatTotal > 0 &&
        Math.abs(priceBeforeDiscount - (itemsTotal + meetAssistTotal)) <
        Math.abs(priceBeforeDiscount - (itemsTotal + childSeatTotal + meetAssistTotal));

    // Fare = everything that is not an add-on, so that fare + add-ons always adds up to
    // the total on display. The two agree for any booking whose parts still match its
    // stored total; they diverge when an admin set the final price by hand, and then the
    // stored total wins. Summing the vehicles is the fallback for the rare record whose
    // add-ons alone already exceed its stored total.
    const additionalServiceTotal = childSeatTotal + meetAssistTotal;
    const fareFromTotal = priceBeforeDiscount - additionalServiceTotal;
    const transferFeeBeforeDiscount =
        fareFromTotal > 0
            ? fareFromTotal
            : Math.max(itemsTotal - (childSeatsInItemTotals ? childSeatTotal : 0), 0);

    return {
        discount,
        hasDiscount: amount > 0,
        labelKeys: getDiscountLabelKeys(discount),
        priceBeforeDiscount,
        totalPrice,
        transferFeeBeforeDiscount,
        transferFee: Math.max(transferFeeBeforeDiscount - amount, 0),
        additionalServiceTotal,
        childSeatCount,
        childSeatTotal,
        meetAssistTotal,
        childSeatsInItemTotals,
    };
};
